import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  CartesianGrid,
} from 'recharts'
import type { IndicatorsResult } from '../../types'

interface Props {
  data: IndicatorsResult | null
  loading?: boolean
}

function fmtTs(ms: number) {
  const d = new Date(ms)
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:00`
}

function fmtVal(v: number | null | undefined, dec = 2) {
  if (v == null) return '—'
  return v.toFixed(dec)
}

export default function IndicatorPanel({ data, loading }: Props) {
  if (loading) {
    return (
      <div className="space-y-3 animate-pulse">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-32 bg-trading-border rounded-lg" />
        ))}
      </div>
    )
  }

  if (!data || !data.timestamps?.length) {
    return (
      <div className="flex items-center justify-center h-40 text-trading-muted text-sm">
        データなし
      </div>
    )
  }

  const n = data.timestamps.length
  const step = Math.max(1, Math.floor(n / 6))

  // RSI チャート用データ
  const rsiData = data.timestamps.map((ts, i) => ({
    ts,
    label: i % step === 0 ? fmtTs(ts) : '',
    rsi:   data.rsi[i],
  }))

  // MACD チャート用データ
  const macdData = data.timestamps.map((ts, i) => ({
    ts,
    label:  i % step === 0 ? fmtTs(ts) : '',
    macd:   data.macd[i],
    signal: data.macd_signal[i],
    hist:   data.macd_hist[i],
  }))

  // Stochastic チャート用データ
  const stochData = data.timestamps.map((ts, i) => ({
    ts,
    label:   i % step === 0 ? fmtTs(ts) : '',
    stoch_k: data.stoch_k[i],
    stoch_d: data.stoch_d[i],
  }))

  const latest = data.latest

  const tooltipStyle = {
    backgroundColor: '#1a1f2e',
    border: '1px solid #2a3040',
    borderRadius: '6px',
    fontSize: '11px',
    color: '#c9d1e8',
  }

  return (
    <div className="space-y-4">
      {/* 最新値サマリー */}
      <div className="grid grid-cols-4 gap-2">
        <LatestBadge
          label="RSI(14)"
          value={fmtVal(latest.rsi, 1)}
          color={
            (latest.rsi ?? 50) > 70 ? 'text-trading-red'
            : (latest.rsi ?? 50) < 30 ? 'text-trading-green'
            : 'text-trading-text'
          }
        />
        <LatestBadge label="MACD" value={fmtVal(latest.macd, 4)} />
        <LatestBadge label="BB幅" value={fmtVal((latest.bb_width ?? 0) * 100, 2) + '%'} />
        <LatestBadge
          label="ストキャ K"
          value={fmtVal(latest.stoch_k, 1)}
          color={
            (latest.stoch_k ?? 50) > 80 ? 'text-trading-red'
            : (latest.stoch_k ?? 50) < 20 ? 'text-trading-green'
            : 'text-trading-text'
          }
        />
      </div>

      {/* RSI */}
      <ChartSection title="RSI (14)">
        <ResponsiveContainer width="100%" height={100}>
          <LineChart data={rsiData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2a3040" />
            <XAxis dataKey="label" tick={{ fill: '#6b7a99', fontSize: 10 }} />
            <YAxis domain={[0, 100]} tick={{ fill: '#6b7a99', fontSize: 10 }} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [v?.toFixed(1), 'RSI']} />
            <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="4 2" strokeWidth={1} />
            <ReferenceLine y={30} stroke="#22c55e" strokeDasharray="4 2" strokeWidth={1} />
            <ReferenceLine y={50} stroke="#6b7a99" strokeDasharray="2 4" strokeWidth={1} />
            <Line type="monotone" dataKey="rsi" stroke="#3b82f6" dot={false} strokeWidth={1.5} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </ChartSection>

      {/* MACD */}
      <ChartSection title="MACD (12, 26, 9)">
        <ResponsiveContainer width="100%" height={110}>
          <BarChart data={macdData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2a3040" />
            <XAxis dataKey="label" tick={{ fill: '#6b7a99', fontSize: 10 }} />
            <YAxis tick={{ fill: '#6b7a99', fontSize: 10 }} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [v?.toFixed(5)]} />
            <ReferenceLine y={0} stroke="#6b7a99" strokeWidth={1} />
            <Bar dataKey="hist" opacity={0.8} radius={[2, 2, 0, 0]}>
              {macdData.map((entry, i) => (
                <Cell key={i} fill={(entry.hist ?? 0) >= 0 ? '#22c55e' : '#ef4444'} />
              ))}
            </Bar>
            <Line type="monotone" dataKey="macd"   stroke="#f59e0b" dot={false} strokeWidth={1.5} connectNulls />
            <Line type="monotone" dataKey="signal" stroke="#a78bfa" dot={false} strokeWidth={1.5} connectNulls />
          </BarChart>
        </ResponsiveContainer>
      </ChartSection>

      {/* Stochastic */}
      <ChartSection title="ストキャスティクス (14, 3)">
        <ResponsiveContainer width="100%" height={100}>
          <LineChart data={stochData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2a3040" />
            <XAxis dataKey="label" tick={{ fill: '#6b7a99', fontSize: 10 }} />
            <YAxis domain={[0, 100]} tick={{ fill: '#6b7a99', fontSize: 10 }} />
            <Tooltip contentStyle={tooltipStyle} />
            <ReferenceLine y={80} stroke="#ef4444" strokeDasharray="4 2" strokeWidth={1} />
            <ReferenceLine y={20} stroke="#22c55e" strokeDasharray="4 2" strokeWidth={1} />
            <Line type="monotone" dataKey="stoch_k" stroke="#f59e0b" dot={false} strokeWidth={1.5} name="%K" connectNulls />
            <Line type="monotone" dataKey="stoch_d" stroke="#a78bfa" dot={false} strokeWidth={1.5} name="%D" connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </ChartSection>
    </div>
  )
}

function ChartSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-trading-border bg-trading-panel p-3">
      <p className="text-[10px] text-trading-muted uppercase tracking-widest mb-2">{title}</p>
      {children}
    </div>
  )
}

function LatestBadge({ label, value, color = 'text-trading-text' }: {
  label: string
  value: string
  color?: string
}) {
  return (
    <div className="rounded-lg border border-trading-border bg-trading-panel p-2 text-center">
      <p className="text-[9px] text-trading-muted mb-0.5">{label}</p>
      <p className={`text-xs font-mono font-semibold ${color}`}>{value}</p>
    </div>
  )
}
